import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import Modal from '@mui/material/Modal';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { getCountries, getStates } from 'country-state-picker';
import { IoClose as Close } from 'react-icons/io5';
import {
  IoPersonOutline as PersonIcon,
  IoCallOutline as ContactIcon,
  IoLocationOutline as LocationIcon,
  IoBriefcaseOutline as ProfessionalIcon,
  IoDocumentTextOutline as RemarksIcon,
} from 'react-icons/io5';
import { ReactComponent as Edit } from '@Assets/editIcon.svg';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
} from '@mui/material';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import BorderWhiteButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderWhiteButtonV2';
import ErrorHandler from '@Root/ErrorHandler';
import NameCardService from '@Helpers/URLHelpers/WeboutService/NameCardService';
import OrganizationCardService from '@Helpers/URLHelpers/WeboutService/OrganizationCardService';
import OrganizationCardForm from '@RegistriesForms/OrganizationCardForm';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import PhoneInput, { isValidPhoneNumber, isPossiblePhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './NameCardView.scss';

export default function NameCardView(props) {
  const [showLoader, setShowLoader] = useState(true);
  const [nameCard, setNameCard] = useState({});
  const [cursor, setCursor] = useState();
  const [name, setName] = useState('');
  const [editName, setEditName] = useState(false);
  const [editOrganization, setEditOrganization] = useState(false);
  const [editDesignation, setEditDesignation] = useState(false);
  const [editEmail, setEditEmail] = useState(false);
  const [editContactNumber, setEditContactNumber] = useState(false);
  const [editDepartment, setEditDepartment] = useState(false);
  const [editBranch, setEditBranch] = useState(false);
  const [editAddress, setEditAddress] = useState(false);
  const [editPinCode, setEditPinCode] = useState(false);
  const [editCountry, setEditCountry] = useState(false);
  const [editState, setEditState] = useState(false);
  const [editRemarks, setEditRemarks] = useState(false);
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [department, setDepartment] = useState('');
  const [branch, setBranch] = useState('');
  const [state, setState] = useState('');
  const [, setCountryCode] = useState('');
  const [states, setStates] = useState([]);
  const [userCountry, setUserCountry] = useState();
  const [, setCountry] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [, setOrganization] = useState(0);
  const [organizationCard, setOrganizationCard] = useState();
  const [showOrganizationCardForm, setShowOrganizationCardForm] = useState(false);
  const [organizationCardList, setOrganizationCardList] = useState([]);
  const [designation, setDesignation] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [areaCode, setAreaCode] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [remarks, setRemarks] = useState('');
  const [edited, setEdited] = useState(false);
  const [isPossibleNumber, setIsPossibleNumber] = useState(false);
  const [isValidNumber, setIsValidNumber] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(true);

  const countries = getCountries();
  const errorHandler = new ErrorHandler();
  const nameCardService = new NameCardService();
  const organizationCardService = new OrganizationCardService();
  const { currentProjectId } = useReduxProject();
  const cn = 'nameCardView';

  const normalizeAreaCode = (value) => {
    const digits = (value || '').toString().replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  };

  const buildInternationalPhoneValue = (contactNumberValue, areaCodeValue) => {
    const normalizedContactNumber = (contactNumberValue || '').toString().replace(/\D/g, '');
    const normalizedAreaCode = (areaCodeValue || '').toString().replace(/\D/g, '');
    if (!normalizedContactNumber) return '';
    const combinedDigits = normalizedAreaCode && !normalizedContactNumber.startsWith(normalizedAreaCode)
      ? `${normalizedAreaCode}${normalizedContactNumber}`
      : normalizedContactNumber;
    return `+${combinedDigits}`;
  };

  const extractNationalPhoneValue = (phoneValue, areaCodeValue) => {
    const normalizedPhoneValue = (phoneValue || '').toString().replace(/\D/g, '');
    const normalizedAreaCode = (areaCodeValue || '').toString().replace(/\D/g, '');
    if (!normalizedPhoneValue) return '';
    if (normalizedAreaCode && normalizedPhoneValue.startsWith(normalizedAreaCode)) {
      return normalizedPhoneValue.slice(normalizedAreaCode.length);
    }
    return normalizedPhoneValue;
  };

  useEffect(() => {
    parseData();
    renderData();
  }, [props]);

  const renderData = async () => {
    try {
      let organizationCardListObj = await organizationCardService.getListByProject(currentProjectId);
      organizationCardListObj = JSON.parse(organizationCardListObj.body);
      setOrganizationCardList(organizationCardListObj || []);
      if (props.nameCard?.organization && props.nameCard.organization !== 0) {
        let orgCardObj = await organizationCardService.getById(props.nameCard.organization);
        orgCardObj = JSON.parse(orgCardObj.body);
        setOrganizationCard(orgCardObj);
      }
    } catch (err) {
      errorHandler.handleError(err);
    }
    setShowLoader(false);
  };

  const parseData = () => {
    const initialAreaCode = normalizeAreaCode(props.nameCard?.areaCode || '');
    const initialPhoneValue = buildInternationalPhoneValue(props.nameCard?.contactNumber, initialAreaCode);
    setImageUrl(
      props.nameCard?.photo
        ? URL.createObjectURL(dataURLtoFile(props.nameCard.photo))
        : ''
    );
    setNameCard(props.nameCard);
    setName(props.nameCard?.name || '');
    setEmail(props.nameCard?.email || '');
    setOrganization(props.nameCard?.organization);
    setDesignation(props.nameCard?.designation || '');
    setDepartment(props.nameCard?.department || '');
    setBranch(props.nameCard?.branch || '');
    setContactNumber(initialPhoneValue);
    setAreaCode(initialAreaCode);
    setIsPossibleNumber(initialPhoneValue ? isPossiblePhoneNumber(initialPhoneValue) : false);
    setIsValidNumber(initialPhoneValue ? isValidPhoneNumber(initialPhoneValue) : false);
    setIsValidEmail(emailValidCheck(props.nameCard?.email || ''));
    setAddress(props.nameCard?.address || '');
    setCountry(props.nameCard?.country || '');
    setCountryCode(props.nameCard?.countryCode || '');
    if (props.nameCard?.countryCode) setStates(getStates(props.nameCard.countryCode));
    setState(props.nameCard?.state || '');
    setUserCountry({ name: props.nameCard?.country || '', code: props.nameCard?.countryCode || '' });
    setPinCode(props.nameCard?.pinCode || '');
    setRemarks(props.nameCard?.remarks || '');
  };

  const emailValidCheck = (email) => {
    const validRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
    return email && email.match(validRegex);
  };

  const genericToggle = (flagSetter, flag, submit = true) => {
    if (flag && submit) handleSubmit();
    flagSetter(!flag);
  };

  const handleNameChange = (e) => {
    const sanitized = DOMPurify.sanitize(e.target.value);
    setName(sanitized);
    nameCard.name = sanitized;
    setEdited(true);
  };

  const handleEmailChange = (e) => {
    const sanitized = DOMPurify.sanitize(e.target.value);
    setEmail(sanitized);
    setIsValidEmail(emailValidCheck(sanitized));
    nameCard.email = sanitized;
    setEdited(true);
  };

  const handleOrganizationChange = (e, value) => {
    if (!value) return;
    setOrganization(value.id);
    setOrganizationCard(value);
    nameCard.organization = value.id;
    setEdited(true);
  };

  const handleDesignationChange = (e) => {
    const sanitized = DOMPurify.sanitize(e.target.value);
    setDesignation(sanitized);
    nameCard.designation = sanitized;
    setEdited(true);
  };

  const handleContactNumberChange = (newNumber) => {
    let parsedPhone = null;
    let nextAreaCode = areaCode;
    if (newNumber) {
      try {
        parsedPhone = parsePhoneNumber(newNumber);
        nextAreaCode = parsedPhone ? `+${parsedPhone.countryCallingCode}` : areaCode;
      } catch {
        parsedPhone = null;
      }
    }
    const nationalPhoneValue = parsedPhone
      ? parsedPhone.nationalNumber
      : extractNationalPhoneValue(newNumber, nextAreaCode);
    setContactNumber(newNumber || '');
    setAreaCode(nextAreaCode);
    setIsPossibleNumber(newNumber && isPossiblePhoneNumber(newNumber));
    setIsValidNumber(newNumber && isValidPhoneNumber(newNumber));
    nameCard.contactNumber = nationalPhoneValue;
    nameCard.areaCode = nextAreaCode;
    setEdited(true);
  };

  const handleDepartmentChange = (e) => {
    const sanitized = DOMPurify.sanitize(e.target.value);
    setDepartment(sanitized);
    nameCard.department = sanitized;
    setEdited(true);
  };

  const handleBranchChange = (e) => {
    const sanitized = DOMPurify.sanitize(e.target.value);
    setBranch(sanitized);
    nameCard.branch = sanitized;
    setEdited(true);
  };

  const handleAddressChange = (e) => {
    const sanitized = DOMPurify.sanitize(e.target.value);
    setAddress(sanitized);
    nameCard.address = sanitized;
    setEdited(true);
  };

  const handlePinCodeChange = (e) => {
    const re = /^[0-9\b]+$/;
    if (e.target.value === '' || re.test(e.target.value)) {
      setPinCode(e.target.value);
      nameCard.pinCode = e.target.value;
      setEdited(true);
    }
  };

  const handleCountryChange = (e, value) => {
    if (!value) return;
    setStates(getStates(value.code));
    setCountry(value.name);
    setCountryCode(value.code);
    setUserCountry(value);
    setState('');
    nameCard.country = value.name;
    nameCard.countryCode = value.code;
    nameCard.state = '';
    setEdited(true);
  };

  const handleStateChange = (e, value) => {
    setState(value || '');
    nameCard.state = value || '';
    setEdited(true);
  };

  const handleRemarksChange = (e) => {
    const sanitized = DOMPurify.sanitize(e.target.value);
    setRemarks(sanitized);
    nameCard.remarks = sanitized;
    setEdited(true);
  };

  const handleFileChange = async (e) => {
    e?.preventDefault();
    const file = e.target.files?.[0];
    if (!file) return;
    if (validateImage(file)) {
      const base64File = await convertFileToBase64(file);
      setImageUrl(URL.createObjectURL(file));
      nameCard.photo = base64File;
      setEdited(true);
      setUploadStatus('');
    } else {
      setUploadStatus('Invalid image');
    }
  };

  const validateImage = (file) => {
    if (file.type.split('/')[0] === 'image') {
      if (file.size > 1024 * 1024) {
        setUploadStatus('Image size exceeded 1MB!');
        return false;
      }
      return true;
    }
    setUploadStatus('Upload image file!');
    return false;
  };

  const convertFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const dataURLtoFile = (dataurl) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], 'file', { type: mime });
  };

  const handleSubmit = async (e) => {
    try {
      e?.preventDefault();
      if (!isValidEmail) return;
      setCursor('wait');
      const parsedPhone = contactNumber ? parsePhoneNumber(contactNumber) : null;
      const nextAreaCode = parsedPhone ? `+${parsedPhone.countryCallingCode}` : areaCode;
      const nextContactNumber = parsedPhone
        ? parsedPhone.nationalNumber
        : extractNationalPhoneValue(contactNumber, nextAreaCode);
      nameCard.areaCode = nextAreaCode;
      nameCard.contactNumber = nextContactNumber;
      await nameCardService.updateNameCard({ nameCard });
      await new Promise((res) => setTimeout(res, 300));
      props.reloadData();
    } catch (err) {
      errorHandler.handleError(err);
    }
    setCursor('auto');
    props.closePopup();
  };

  const toggleShowOrganizationCardForm = () => setShowOrganizationCardForm(!showOrganizationCardForm);

  const getInitials = (name) => {
    if (!name) return 'NC';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const heroMeta = [designation, organizationCard?.name].filter(Boolean).join(' · ');

  // ─── Sub-components ─────────────────────────────────────────────────────────

  const Section = ({ title, icon, children }) => (
    <Box className={`${cn}__section`}>
      <Box className={`${cn}__section__head`}>
        {icon}
        <span>{title}</span>
      </Box>
      <Box className={`${cn}__section__body`}>
        {children}
      </Box>
    </Box>
  );

  const FieldRow = ({ label, editing, toggle, valueDisplay, error, children }) => (
    <Box className={`${cn}__fieldRow ${editing ? `${cn}__fieldRow--editing` : ''}`}>
      <Box className={`${cn}__fieldRow__labelCol`}>
        <Typography className={`${cn}__fieldRow__label`}>{label}</Typography>
      </Box>
      <Box className={`${cn}__fieldRow__valueCol`}>
        {editing ? (
          <Box className={`${cn}__fieldRow__input`} onBlur={toggle}>
            {children}
            {error && (
              <Typography className={`${cn}__error`}>{error}</Typography>
            )}
          </Box>
        ) : (
          <Typography
            className={`${cn}__fieldRow__value ${!valueDisplay ? `${cn}__muted` : ''}`}
            onClick={toggle}
          >
            {valueDisplay || '—'}
          </Typography>
        )}
      </Box>
      <Tooltip title={editing ? 'Save' : 'Edit'}>
        <IconButton size="small" onClick={toggle} className={`${cn}__editBtn`}>
          <Edit />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Modal open={props.open} onClose={props.closePopup} style={{ cursor }}>
      <div className={cn}>
        <div className={`${cn}__panel height-wfa`}>

          {/* ── Hero ─────────────────────────────────────────────────────── */}
          <Box className={`${cn}__hero`}>
            <Tooltip title="Change photo">
              <Box
                className={`${cn}__hero__photoWrap`}
                onClick={() => document.getElementById('fileInputNamePhoto').click()}
              >
                <Avatar src={imageUrl} className={`${cn}__hero__avatar`} variant="rounded">
                  {!imageUrl && getInitials(name)}
                </Avatar>
                <Box className={`${cn}__hero__overlay`}>
                  <PhotoCameraIcon />
                </Box>
              </Box>
            </Tooltip>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              id="fileInputNamePhoto"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Typography className={`${cn}__hero__name`}>
              {name || 'Name Card'}
            </Typography>
            {heroMeta && (
              <Typography className={`${cn}__hero__meta`}>{heroMeta}</Typography>
            )}
            {uploadStatus && (
              <Typography className={`${cn}__error ${cn}__error--hero`}>{uploadStatus}</Typography>
            )}

            <IconButton onClick={props.closePopup} className={`${cn}__closeBtn`}>
              <Close />
            </IconButton>
          </Box>

          {/* ── Form ─────────────────────────────────────────────────────── */}
          {showLoader ? (
            <Box className={`${cn}__loader`}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <form onSubmit={handleSubmit} className={`${cn}__form`}>

              <Section title="Identity" icon={<PersonIcon />}>
                <FieldRow
                  label="Name"
                  editing={editName}
                  toggle={() => genericToggle(setEditName, editName)}
                  valueDisplay={name}
                >
                  <TextField
                    value={name}
                    onChange={handleNameChange}
                    size="small"
                    fullWidth
                    placeholder="Enter name"
                    inputProps={{ maxLength: 200 }}
                    autoFocus
                  />
                </FieldRow>
                <FieldRow
                  label="Email"
                  editing={editEmail}
                  toggle={() => genericToggle(setEditEmail, editEmail)}
                  valueDisplay={email}
                  error={!isValidEmail ? 'Please enter a valid email' : ''}
                >
                  <TextField
                    value={email}
                    onChange={handleEmailChange}
                    size="small"
                    fullWidth
                    type="email"
                    placeholder="Enter email"
                    inputProps={{ maxLength: 200 }}
                    error={!isValidEmail}
                    autoFocus
                  />
                </FieldRow>
              </Section>

              <Section title="Contact" icon={<ContactIcon />}>
                <FieldRow
                  label="Phone"
                  editing={editContactNumber}
                  toggle={() => genericToggle(setEditContactNumber, editContactNumber)}
                  valueDisplay={contactNumber}
                  error={contactNumber && (!isPossibleNumber || !isValidNumber) ? 'Invalid phone number' : ''}
                >
                  <PhoneInput
                    international
                    defaultCountry="IN"
                    value={contactNumber}
                    onChange={handleContactNumberChange}
                    className={`${cn}__phoneInput`}
                  />
                </FieldRow>
              </Section>

              <Section title="Professional" icon={<ProfessionalIcon />}>
                <FieldRow
                  label="Organization"
                  editing={editOrganization}
                  toggle={() => genericToggle(setEditOrganization, editOrganization)}
                  valueDisplay={organizationCard?.name}
                >
                  <Autocomplete
                    options={organizationCardList}
                    value={organizationCard || null}
                    onChange={handleOrganizationChange}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={(p) => <TextField {...p} placeholder="Select organization" size="small" />}
                  />
                  <Typography className={`${cn}__link`} onClick={toggleShowOrganizationCardForm}>
                    + Create an Organization Card
                  </Typography>
                </FieldRow>
                <FieldRow
                  label="Designation"
                  editing={editDesignation}
                  toggle={() => genericToggle(setEditDesignation, editDesignation)}
                  valueDisplay={designation}
                >
                  <TextField
                    value={designation}
                    onChange={handleDesignationChange}
                    size="small"
                    fullWidth
                    placeholder="Enter designation"
                    inputProps={{ maxLength: 200 }}
                    autoFocus
                  />
                </FieldRow>
                <FieldRow
                  label="Department"
                  editing={editDepartment}
                  toggle={() => genericToggle(setEditDepartment, editDepartment)}
                  valueDisplay={department}
                >
                  <TextField
                    value={department}
                    onChange={handleDepartmentChange}
                    size="small"
                    fullWidth
                    placeholder="Enter department"
                    inputProps={{ maxLength: 200 }}
                    autoFocus
                  />
                </FieldRow>
                <FieldRow
                  label="Branch"
                  editing={editBranch}
                  toggle={() => genericToggle(setEditBranch, editBranch)}
                  valueDisplay={branch}
                >
                  <TextField
                    value={branch}
                    onChange={handleBranchChange}
                    size="small"
                    fullWidth
                    placeholder="Enter branch"
                    inputProps={{ maxLength: 200 }}
                    autoFocus
                  />
                </FieldRow>
              </Section>

              <Section title="Location" icon={<LocationIcon />}>
                <FieldRow
                  label="Address"
                  editing={editAddress}
                  toggle={() => genericToggle(setEditAddress, editAddress)}
                  valueDisplay={address}
                >
                  <TextField
                    value={address}
                    onChange={handleAddressChange}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    placeholder="Enter address"
                    autoFocus
                  />
                </FieldRow>
                <FieldRow
                  label="Country"
                  editing={editCountry}
                  toggle={() => genericToggle(setEditCountry, editCountry)}
                  valueDisplay={nameCard?.country}
                >
                  <Autocomplete
                    options={countries}
                    value={userCountry || null}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.code === v.code}
                    onChange={handleCountryChange}
                    renderInput={(p) => <TextField {...p} placeholder="Select country" size="small" />}
                  />
                </FieldRow>
                <FieldRow
                  label="State"
                  editing={editState}
                  toggle={() => genericToggle(setEditState, editState)}
                  valueDisplay={nameCard?.state}
                >
                  <Autocomplete
                    options={states}
                    value={state || null}
                    getOptionLabel={(o) => o}
                    isOptionEqualToValue={(o, v) => o === v}
                    onChange={handleStateChange}
                    renderInput={(p) => <TextField {...p} placeholder="Select state" size="small" />}
                  />
                </FieldRow>
                <FieldRow
                  label="Pin Code"
                  editing={editPinCode}
                  toggle={() => genericToggle(setEditPinCode, editPinCode)}
                  valueDisplay={pinCode}
                >
                  <TextField
                    value={pinCode}
                    onChange={handlePinCodeChange}
                    size="small"
                    fullWidth
                    placeholder="Enter pin code"
                    inputProps={{ maxLength: 20 }}
                    autoFocus
                  />
                </FieldRow>
              </Section>

              <Section title="Notes" icon={<RemarksIcon />}>
                <FieldRow
                  label="Remarks"
                  editing={editRemarks}
                  toggle={() => genericToggle(setEditRemarks, editRemarks)}
                  valueDisplay={remarks}
                >
                  <TextField
                    value={remarks}
                    onChange={handleRemarksChange}
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={6}
                    placeholder="Enter any remarks"
                    autoFocus
                  />
                </FieldRow>
              </Section>

            </form>
          )}

          {/* ── Actions ──────────────────────────────────────────────────── */}
          <Box className={`${cn}__actions`}>
            <BorderBlueButtonV2
              text="Update"
              onClick={handleSubmit}
              disabled={cursor === 'wait' || showLoader || !edited || !isValidEmail}
            />
            <BorderWhiteButtonV2
              text="Cancel"
              onClick={props.closePopup}
              disabled={cursor === 'wait'}
            />
          </Box>

        </div>

        {showOrganizationCardForm && (
          <OrganizationCardForm
            open={showOrganizationCardForm}
            closePopup={toggleShowOrganizationCardForm}
            reloadData={renderData}
          />
        )}
      </div>
    </Modal>
  );
}
